import { formatAddress } from '@frontend/api-client';
import type { SecureNote } from '@frontend/api-types';
import { dateFormatters, e164ToInternational, formatCurrencyNatural } from '@frontend/duck-tape';
import { useEffect, useState } from '@frontend/react';
import { useVisibilityController } from '@frontend/react/hooks';
import {
  Clickable,
  Divider,
  EmptyList,
  FlatList,
  Header,
  Icon,
  LinkButton,
  PageContainer,
  SkeletonList,
  Text,
  XStack,
  YStack,
} from '@frontend/web-react';
import { CreateSecureNoteModal, UpdateSecureNoteModal } from '@frontend/web/components';
import { UpdatePrimaryAddressModal } from '@frontend/web/components/DomainSpecific/Settings';
import { UpdateProfileModal } from '@frontend/web/components/DomainSpecific/Settings/UpdateProfileModal';
import {
  useLazyListPurchasesQuery,
  useLazyListSecureNotesQuery,
  useListAddressesQuery,
  useRetrieveClientQuery,
} from '@frontend/web/hooks';
import { createFileRoute, useRouter } from '@tanstack/react-router';

const Settings = () => {
  const router = useRouter();
  const { data: client, isLoading: isClientLoading } = useRetrieveClientQuery();
  const { data: addresses, isLoading: isAddressesLoading } = useListAddressesQuery();
  const [listSecureNotes, { data: secureNotes, isLoading: isListSecureNotesLoading }] = useLazyListSecureNotesQuery();
  const [listPurchases, { data: purchases, isLoading: isListPurchasesLoading }] = useLazyListPurchasesQuery();

  const [secureNotesSliceAmount, setSecureNotesSliceAmount] = useState(5);
  const [purchasesSliceAmount, setPurchasesSliceAmount] = useState(5);
  const [selectedSecureNote, setSelectedSecureNote] = useState<SecureNote>();
  const [isEditSecureNoteModalOpen, { close: closeEditSecureNoteModal, open: openEditSecureNoteModal }] =
    useVisibilityController();
  const [isCreateSecureNoteModalOpen, { close: closeCreateSecureNoteModal, open: openCreateSecureNoteModal }] =
    useVisibilityController();
  const [isEditProfileModalOpen, { close: closeEditProfileModal, open: openEditProfileModal }] =
    useVisibilityController();
  const [isEditAddressModalOpen, { close: closeEditAddressModal, open: openEditAddressModal }] =
    useVisibilityController();

  useEffect(() => {
    if (client?.id) {
      listPurchases({ clientId: client?.id });
    }
  }, [client?.id]);

  useEffect(() => {
    if (client?.id) {
      listSecureNotes({ clientId: client?.id });
    }
  }, [client?.id]);

  const onClickShowMoreSecureNotes = () => setSecureNotesSliceAmount((prev) => prev + 5);

  const onClickShowMorePurchases = () => setPurchasesSliceAmount((prev) => prev + 5);

  const onClickPurchaseCard = (taskId: string) => router.navigate({ params: { taskId }, to: '/app/task/$taskId' });

  const address = addresses?.[0];

  return (
    <PageContainer
      LoadingComponent={<SkeletonList className="pt-lg" countSkeletons={6} />}
      className="bg-neutral-50"
      isLoading={!client || isClientLoading || isAddressesLoading || isListPurchasesLoading || isListSecureNotesLoading}
      isScrollable
      padding="none"
    >
      <Header className="pl-sm" title="Profile" titleSize="h4" />
      {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
      <YStack className="p-lg pt-none xl:max-w-[1000px]" gapY="sm">
        <YStack gapY="md">
          <YStack className="gap-lg border-1 border-borderPrimary rounded-lg p-md bg-trueWhite">
            <YStack className="gap-md">
              <XStack>
                <Text type="h4">
                  {client?.firstName} {client?.lastName}
                </Text>
              </XStack>
              <YStack className="gap-sm">
                <XStack alignItems="center" gapX="xs">
                  <Icon color="secondary" name="IconMail" size="sm" />
                  <Text color="secondary" type="caption">
                    {client?.email}
                  </Text>
                </XStack>
                <XStack alignItems="center" gapX="xs">
                  <Icon color="secondary" name="IconPhone" size="sm" />
                  {client?.phoneNumber ? (
                    <Text color="secondary" type="caption">
                      {e164ToInternational(client.phoneNumber) ?? 'Invalid phone number'}
                    </Text>
                  ) : null}
                </XStack>
                <XStack alignItems="center" gapX="xs">
                  <Icon color="secondary" name="IconHome" size="sm" />
                  <Text color="secondary" type="caption">
                    {address ? formatAddress(address) : null}
                  </Text>
                </XStack>
                {client?.birthday ? (
                  <XStack alignItems="center" gapX="xs">
                    <Icon color="secondary" name="IconCake" size="sm" />
                    <Text color="secondary" type="caption">
                      {dateFormatters['MM/DD/YYYY'](client.birthday)}
                    </Text>
                  </XStack>
                ) : null}
                <YStack alignItems="start" className="self-end" gapY="sm" justifyContent="end">
                  <LinkButton
                    color="secondary"
                    label="Edit Profile"
                    leftIconName="IconPencil"
                    onClick={openEditProfileModal}
                  />
                  <LinkButton
                    color="secondary"
                    label="Edit Address"
                    leftIconName="IconPencil"
                    onClick={openEditAddressModal}
                  />
                </YStack>
              </YStack>
            </YStack>
          </YStack>
        </YStack>
        <YStack className="mt-sm" gapY="md">
          <Text type="h5">Secure Info</Text>
          <YStack gapY="sm">
            <FlatList
              ListEmptyComponent={
                <EmptyList
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  className="h-auto gap-y-sm min-h-[200px]"
                  iconName="IconLock"
                  label="No secure notes yet"
                  subLabel="We keep your sensitive info safe and sound, using top-notch encryption and strict access rules - only our trained copilots get a peek."
                />
              }
              ListFooterComponent={
                <YStack className="mt-md" gapY="sm">
                  <Clickable
                    alignItems="center"
                    className="bg-trueWhite p-md border-borderPrimary border-1 rounded-lg border-dashed"
                    enableHoverEffect
                    flexDirection="row"
                    gapX="sm"
                    onClick={openCreateSecureNoteModal}
                  >
                    <XStack alignItems="center" className="flex-1" gapX="sm">
                      <Icon name="IconLock" size={24} />
                      <Text type="h5">Add New</Text>
                    </XStack>
                    <Icon name="IconPlus" />
                  </Clickable>
                  <XStack justifyContent="end">
                    {secureNotes?.length && secureNotes?.length > secureNotesSliceAmount ? (
                      <Clickable
                        className="py-sm px-md rounded-md"
                        enableHoverEffect
                        onClick={onClickShowMoreSecureNotes}
                      >
                        <Text color="secondary" type="h5">
                          Show more
                        </Text>
                      </Clickable>
                    ) : null}
                  </XStack>
                </YStack>
              }
              contentContainerClassName="gap-sm"
              data={secureNotes?.slice(0, secureNotesSliceAmount)}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <Clickable
                  alignItems="center"
                  className="p-md border-borderPrimary border-1 rounded-lg bg-trueWhite"
                  enableHoverEffect
                  flexDirection="row"
                  gapX="sm"
                  onClick={() => {
                    setSelectedSecureNote(item);
                    openEditSecureNoteModal();
                  }}
                >
                  <XStack alignItems="center" className="flex-1" gapX="sm">
                    <Icon name="IconLock" size={24} />
                    <Text type="h5">{item.title}</Text>
                  </XStack>
                  <Icon name="IconChevronRight" />
                </Clickable>
              )}
            />
          </YStack>
        </YStack>
        <Divider />
        <YStack className="mt-sm" gapY="md">
          <Text type="h5">Purchases</Text>
          <YStack gapY="sm">
            <FlatList
              ListEmptyComponent={
                <EmptyList
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  className="h-auto gap-y-sm min-h-[200px]"
                  iconName="IconShoppingBag"
                  label="No purchases yet"
                  subLabel="When Duckbill makes any purchases for you, it'll show up here."
                />
              }
              ListFooterComponent={
                <XStack className="mt-md" justifyContent="end">
                  {purchases?.length && purchases?.length > purchasesSliceAmount ? (
                    <Clickable className="py-sm px-md rounded-md" enableHoverEffect onClick={onClickShowMorePurchases}>
                      <Text color="secondary" type="h5">
                        Show more
                      </Text>
                    </Clickable>
                  ) : null}
                </XStack>
              }
              contentContainerClassName="gap-sm"
              data={purchases?.slice(0, purchasesSliceAmount)}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <Clickable
                  className="p-md border-borderPrimary border-1 rounded-lg bg-trueWhite"
                  enableHoverEffect
                  gapX="sm"
                  gapY="md"
                  onClick={() => onClickPurchaseCard(item.task)}
                >
                  <YStack gapY="xs">
                    <XStack alignItems="center">
                      <XStack className="flex-1">
                        <Text type="h5">{item.item}</Text>
                      </XStack>
                      <Text>{formatCurrencyNatural(item.amount)}</Text>
                    </XStack>
                    <Text color="tertiary" type="p-sm">
                      {item.vendor}
                    </Text>
                  </YStack>
                  <XStack alignItems="center" justifyContent="spaceBetween">
                    <Text color="tertiary" type="caption">
                      {dateFormatters['MM/DD/YYYY'](item.createdAt)}
                    </Text>
                    <Text className="underline" type="h5">
                      View Task
                    </Text>
                  </XStack>
                </Clickable>
              )}
            />
          </YStack>
        </YStack>
      </YStack>
      <UpdateProfileModal isOpen={isEditProfileModalOpen} onClose={closeEditProfileModal} />
      {address ? (
        <UpdatePrimaryAddressModal
          isOpen={isEditAddressModalOpen}
          onClose={closeEditAddressModal}
          primaryAddress={address}
        />
      ) : null}
      {selectedSecureNote ? (
        <UpdateSecureNoteModal
          isOpen={isEditSecureNoteModalOpen}
          onClose={closeEditSecureNoteModal}
          secureNoteId={selectedSecureNote.id}
          title={selectedSecureNote.title}
        />
      ) : null}
      {client ? (
        <CreateSecureNoteModal
          clientId={client.id}
          isOpen={isCreateSecureNoteModalOpen}
          onClose={closeCreateSecureNoteModal}
        />
      ) : null}
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/settings/')({
  component: Settings,
});
